/**
 * Import v2 유효성 검증 (v2.7)
 *
 * CSV/JSON 레코드에 대해 필수/패턴/길이 검증.
 * 검증 규칙은 `utils/recordValidation.validateFieldValue`에 중앙화되어 있음 (Record Edit과 동일 규칙).
 */

import { validateFieldValue } from 'utils/recordValidation'
import { TITLE_FIELD } from './importReducer'
import { itemTypeToUiType } from './importUtils'

const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/

/**
 * 제목 필수 검증 결과 에러 객체 생성
 */
const makeTitleError = (id, row) => ({
  id,
  row,
  field: TITLE_FIELD,
  value: '',
  reason: 'requiredEmpty',
  messageKey: 'validation.required.invalid',
  messageParams: {},
  uiType: 'text',
  minLength: null,
  action: null,
  fix: '',
  editError: null,
})

/**
 * 단일 item 검증 → 에러 객체 또는 null
 */
const validateItemAndMakeError = (item, row, errorId, isRequired, uiType) => {
  const result = validateFieldValue(uiType, item.content, {
    required: isRequired,
    minLength: item.minLength ?? null,
    maxLength: item.maxLength ?? null,
  })
  if (result.valid) return null
  return {
    id: errorId,
    row,
    field: item.label || item.type,
    value: String(item.content || ''),
    reason: result.reason,
    messageKey: result.messageKey,
    messageParams: result.messageParams,
    uiType, // 재검증 시 원래 타입 참조
    minLength: item.minLength ?? null,
    maxLength: item.maxLength ?? null,
    action: null,
    fix: '',
    editError: null,
  }
}

/**
 * CSV 기반 레코드 배열 검증 (Google/Custom 프리셋)
 */
export const validateCsvRecords = (records, preset, mappings) => {
  const errors = []
  const validRecords = []
  let errorId = 1

  // 필수 라벨 set (Google·템플릿 양식 프리셋의 매핑 기반 — 템플릿의 required 필드 빈값을 행 오류로 본다)
  const requiredLabels = new Set()
  if (preset.id === 'google' || preset.id === 'template') {
    mappings.forEach((m) => {
      if (m.required && m.field) requiredLabels.add(m.field)
    })
  }

  records.forEach((record, idx) => {
    const row = idx + 1
    let hasError = false

    if (!record.title || !record.title.trim()) {
      errors.push(makeTitleError(errorId, row))
      errorId += 1
      hasError = true
    }

    if (record.items) {
      record.items.forEach((item) => {
        const fieldName = item.label || item.type
        const isRequired = requiredLabels.has(fieldName)
        // uiType 결정: convertMappingToRecords가 주입. 테스트/하위호환 fallback은 item.type 사용
        const uiType = item.uiType || item.type || 'text'
        const err = validateItemAndMakeError(item, row, errorId, isRequired, uiType)
        if (err) {
          errors.push(err)
          errorId += 1
          hasError = true
        }
      })
    }

    if (!hasError) validRecords.push(record)
  })

  return { errors, validRecords }
}

/**
 * JSON 기반 레코드 배열 검증 (OfficePassword 프리셋)
 *
 * typeList 기반 uiType 복원 후 validateFieldValue 호출.
 */
export const validateJsonRecords = (records, typeList = []) => {
  const errors = []
  const validRecords = []
  let errorId = 1

  records.forEach((record, idx) => {
    const row = idx + 1
    let hasError = false

    if (!record.title || !record.title.trim()) {
      errors.push(makeTitleError(errorId, row))
      errorId += 1
      hasError = true
    }

    if (record.items) {
      record.items.forEach((item) => {
        const typeInfo = typeList?.find?.((t) => t.type_name === item.type) || null
        const uiType = itemTypeToUiType(item.type, item.encrypt, typeInfo?.format)
        const itemWithMeta = {
          ...item,
          minLength: typeInfo?.min_length ?? null,
          maxLength: typeInfo?.max_length ?? null,
        }
        // OfficePassword: required는 제외 (원본 템플릿의 required_uids 재조회 필요)
        const err = validateItemAndMakeError(itemWithMeta, row, errorId, false, uiType)
        if (err) {
          errors.push(err)
          errorId += 1
          hasError = true
        }
      })
    }

    if (!hasError) validRecords.push(record)
  })

  return { errors, validRecords }
}

/**
 * 통합 검증 함수
 * @param {Array} typeList - v2.7: OfficePassword 경로에서 uiType 복원용 data_types
 */
export const validateRecords = (records, preset, mappings, typeList = []) => {
  if (preset.id === 'officepassword') {
    return validateJsonRecords(records, typeList)
  }
  return validateCsvRecords(records, preset, mappings)
}

/**
 * 단일 필드 값 재검증 (사용자 수정 후). v2.7 시그니처 변경.
 *
 * @param {Object|string} err - 에러 객체 (v2.7: reason, uiType, minLength 포함) 또는 reason string(v2.6 하위호환)
 * @param {string} value - 수정된 값
 * @returns {{ valid: boolean, reason: string|null, messageKey: string|null, messageParams: object }}
 *
 * v2.7 변경: boolean → 결과 객체 반환. 재검증 실패 시 **새 에러 타입/메시지**를 UI에 전달.
 * 하위호환: boolean을 기대하던 기존 호출(테스트)은 .valid 접근 or truthy 체크로 동작.
 */
export const revalidateField = (err, value) => {
  const v = (value || '').toString().trim()
  if (!v) {
    return {
      valid: false,
      reason: 'requiredEmpty',
      messageKey: 'validation.required.invalid',
      messageParams: {},
    }
  }

  // v2.7: err이 uiType/minLength를 가지고 있으면 validateFieldValue로 정확 재검증
  if (err && typeof err === 'object' && err.uiType) {
    return validateFieldValue(err.uiType, value, {
      required: true,
      minLength: err.minLength ?? null,
      maxLength: err.maxLength ?? null,
    })
  }

  // 하위 호환: err이 string이면 v2.6 이하 reason 호환 모드
  const reason = typeof err === 'string' ? err : err?.reason
  if (reason === 'dateFormat' || reason === 'formatError') {
    if (DATE_REGEX.test(v)) {
      return { valid: true, reason: null, messageKey: null, messageParams: {} }
    }
    return {
      valid: false,
      reason: 'dateFormat',
      messageKey: 'record.import.v2.validation.error.formatError',
      messageParams: {},
    }
  }
  // requiredEmpty → 값이 있으면 통과
  return { valid: true, reason: null, messageKey: null, messageParams: {} }
}
